/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {SyntheticEvent, useState} from 'react';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import isNull from 'lodash.isnull';
import AlertsNotificationsMapView from '@ion/applications/src/pages/alertsNotifications/AlertsNotificationsMapView';
import {translate} from '@ion/core/src/locale/locale';
import {AlertNotification} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {FormCheckBox} from '@ion/dls/src/components/forms/FormCheckBox';
import {FormLabelValue} from '@ion/dls/src/components/forms/FormLabelValue';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useExtendStyles} from '../hooks/useExtendStyles';
import NotificationViewDialog from './NotificationViewDialog';

export interface ViewAlertNotificationsProps {
  open: boolean;
  notification: AlertNotification | null;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  loading?: boolean;
}

export interface TabPanelProps {
  value?: number;
  index?: number;
  children?: JSX.Element;
}

const styles = (theme: DLSTheme) => ({
  headerSection : {
    padding        : '50px 120px',
    background     : dlsColor('grey6'),
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center'
  },
  panelSection : {
    background : 'white'
  },
  notificationLabel : {
    fontFamily    : 'Roboto',
    fontSize      : '14px',
    fontStyle     : 'normal',
    fontWeight    : 700,
    lineHeight    : '18px',
    letterSpacing : '1px',
    textAlign     : 'left',
    color         : dlsColor('grey3')
  },
  notificationTime : {
    fontFamily    : toggleThemeStyle('Neo Sans Std', 'Inter'),
    fontSize      : '30px',
    fontStyle     : 'normal',
    fontWeight    : '400',
    lineHeight    : '36px',
    letterSpacing : '0px',
    textAlign     : 'left',
    color         : dlsColor('grey1')
  },
  tabStyle : {
    fontFamily    : toggleThemeStyle('Neo Sans Std', 'Inter'),
    fontSize      : '16px',
    fontStyle     : 'normal',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px',
    textAlign     : 'center'
  },
  contentContainer : {
    display    : 'flex',
    flexWrap   : 'wrap',
    background : 'white'
  },
  mapContainer : {
    position  : 'relative',
    height    : '480px',
    width     : '47%',
    marginTop : '77px'
  },
  noLocation : {
    position        : 'absolute',
    bottom          : '0%',
    left            : '20%',
    padding         : '8px',
    textAlign       : 'center',
    color           : dlsColor('white'),
    textTransform   : 'capitalize',
    backgroundColor : dlsColor('grey1')
  }
});

function TabPanel({children, value, index, ...other}: TabPanelProps) {
  return (
    <div style={{background: 'white'}}
      hidden={value !== index}
      {...other}>
      {value === index && (
        <Box sx={{p: 3}}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

/**
 * The ViewAlertNotifications uses to show notifications details in label value pair along with mapview
 * pointing with the marker for the the existing lat & long values
 *
 * @param open: Determines if the Notification dialog should be open
 * @param notification: notification data to be loaded into the view
 * @param onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param loading: disables any for submission while the form is busy
 */
export const ViewAlertNotifications = ({
  open,
  notification,
  onCloseDialog,
  loading = false
}: ViewAlertNotificationsProps) => {
  const editorStyles = useExtendStyles(styles);
  const classes      = editorStyles();

  const [activeTab, setActiveTab] = useState(0);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  };

  return (
    <NotificationViewDialog open={open}
      onCloseDialog={onCloseDialog}>
      <div className={classes.contentContainer}>
        <div className={classes.headerSection} style={{width: '100%'}}>
          <div className={classes.notificationLabel}>{'Notification'}</div>
          <div className={classes.notificationTime}>{notification?.createdOnUI}</div>
        </div>
        <Box sx={{width: '50%'}}>
          <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
            <Tabs style={{marginLeft: '80px'}} value={activeTab} onChange={handleChange}>
              <Tab className={classes.tabStyle} label={translate('alerts-notifications:editors.view.alertHighlights')} />
              <Tab className={classes.tabStyle} label={translate('alerts-notifications:editors.view.additionalInfo')} />
            </Tabs>
          </Box>
          <TabPanel value={activeTab} index={0}>
            <>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.deviceName')}
                value={notification?.deviceUI || notification?.device?.title || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:grid.columns.severity')}
                value={notification?.severity || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:grid.columns.alertType')}
                value={notification?.alertTypeName || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.alertLocation')}
                value={notification?.address?.label || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:grid.columns.esn')}
                value={notification?.esn || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:grid.columns.alertConfiguration')}
                value={notification?.alertConfigurationName || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:grid.columns.created')}
                value={notification?.createdOnUI || translate('common:notAvailable')}/>
            </>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.assetName')}
                value={notification?.assetName || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.deviceType')}
                value={notification?.deviceType || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.acknowledgeBy')}
                value={notification?.acknowledgedUserName || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.isAcknowledged')}
                checkbox={<FormCheckBox required
                  disabled={true}
                  useWrapperStyle={false}
                  defaultChecked={notification?.isAcknowledged}/>}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.acknowledgedOn')}
                value={formatDateTime(notification?.acknowledgedOn) || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.alertAction')}
                value={notification?.alertAction?.title || notification?.alertActions[0]?.alertAction?.title || translate('common:notAvailable')}/>
              <FormLabelValue fullWidth
                label={translate('alerts-notifications:editors.view.triggerEntities')}
                value={notification?.alertTriggers?.reduce(function(accumulator, item) {
                  const result = accumulator;
                  return result + item?.title + ' ';
                }, '') || translate('common:notAvailable')}/>
            </>
          </TabPanel>
        </Box>
        <div className={classes.mapContainer}>
          <AlertsNotificationsMapView notification={notification} setIsMapInitialized={setIsMapInitialized} />
          {isMapInitialized && (isNull(notification?.latitude) || isNull(notification?.longitude)) && (
            <p className={classes.noLocation}>{translate('alerts-notifications:editors.view.noLocation')}</p>
          )}
        </div>
      </div>
    </NotificationViewDialog>
  );
};

export default ViewAlertNotifications;
